import { useAtomValue } from "jotai";
import { NextSeo } from "next-seo";
import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { DefaultTitleTemplate } from "~/components/meta/consts";
import { useDeepLinkTags, useDynamicMetaTags, useKeywordMetaTags } from "~/components/meta/hooks";
import { trackingAtom } from "~/components/tracking";
import type { topicSeoTopic$key } from "~/queries/__generated__/topicSeoTopic.graphql";

type Props = {
  reference: topicSeoTopic$key;
};

export const TopicSeo: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const topic = useFragment(
    graphql`
      fragment topicSeoTopic on Topic {
        entityUuid
        entityId
        sponsor {
          name
        }
        name
        description {
          text
        }
        urlAlias
        metaDescription
        ...hooksUseDeepLinkTagsBase
        ...hooksUseDynamicMetaTags
      }
    `,
    reference_,
  );

  const metaTags = useDynamicMetaTags(topic);
  const keywordTags = useKeywordMetaTags(metaTags.controlled.keywords ?? topic.name);
  const deepLinkTags = useDeepLinkTags(topic);

  let titleTemplate = DefaultTitleTemplate;
  const title = useMemo(
    () => metaTags.controlled.title ?? topic.name,
    [metaTags.controlled.title, topic.name],
  );
  if (metaTags.controlled.title) {
    titleTemplate = "%s";
  }

  const description =
    metaTags.controlled.description ??
    topic.metaDescription ??
    topic.description?.text ??
    undefined;

  // Track page view put here is because it needs the meta title for the page title
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    const { entityId, entityUuid, name, sponsor, urlAlias } = topic;
    trackPageView({
      contentType: "Topic",
      entityId,
      entityUuid,
      page: "Topic",
      pageTitle: title,
      path: urlAlias,
      sponsor: sponsor?.name,
      topicID: entityId,
      topics: name,
    });
  }, [trackPageView, topic, title]);

  return (
    <NextSeo
      additionalMetaTags={[...keywordTags, ...deepLinkTags]}
      description={description}
      title={title}
      titleTemplate={titleTemplate}
    />
  );
};

TopicSeo.displayName = "TopicSeo";
